import { computed, nextTick, ref } from 'vue'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import sendYaGoal from 'src/utils/sendYaGoal'
import type { LpuServicesPricesRequest } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/api/apiLpuServicesPrices.types'
import apiLpuServicesPrices from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/api/apiLpuServicesPrices'
import { PRICE_PAGE_GOAL } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/constants'
import setPageQueryParam from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/functions/setPageQueryParam'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import unwrapMedflexClasses from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/functions/unwrapMedflexClasses'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/types'
import type { MedflexServiceClass } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/types/medflex'
import smoothScrollToSelector from 'src/utils/smoothScrollToSelector'

type SearchKind = 'search_query' | 'service_category_name'

/**
 * Поиск, фильтр онлайн-записи и пагинация полного прайса Medflex.
 *
 * Первый кадр берётся из Django context; API дергается только после
 * действия пользователя. Ответ устаревшего запроса не применяется к state.
 */
export default function useMedflexPrices() {
  const { context }: ContextStore = useContextStore()
  const { reverse } = useURLStore()

  const initialClasses = unwrapMedflexClasses(context.data)
  const apiUrl = reverse({ name: 'ajax_services_prices' })
  const serviceCategories = unwrapList(context.service_categories)

  const serviceClasses = ref<MedflexServiceClass[]>(initialClasses)
  const pageCount = ref(context.num_pages || 1)
  const currentPage = ref(context.current_page || 1)
  const isNotFound = ref(false)
  const isLoading = ref(false)
  const hasUsedSearch = ref(false)
  const isOnlineOnly = ref(Boolean(context.direct_appointment_toggle_state))
  const lastSearch = ref<{ text: string, type: SearchKind }>({
    text: '',
    type: 'search_query',
  })
  const selectedCategoryName = ref('')

  let loadRequestId = 0

  const hasOnlineSwitch = computed(() => context.direct_appointment_toggle_state != null)
  const showPagination = computed(() => pageCount.value > 1)
  const showAllButton = computed(() => (
    isOnlineOnly.value
    && currentPage.value === pageCount.value
  ))

  async function loadClasses(params: LpuServicesPricesRequest) {
    if (!apiUrl) {
      return
    }

    loadRequestId += 1

    const requestId = loadRequestId

    isLoading.value = true

    try {
      const { data } = await apiLpuServicesPrices.get(apiUrl, params)

      if (requestId !== loadRequestId) {
        return
      }

      const classes = unwrapMedflexClasses(data.data)

      pageCount.value = data.num_pages || 1
      currentPage.value = data.current_page || 1
      serviceClasses.value = classes
      isNotFound.value = classes.length === 0
      setPageQueryParam(currentPage.value)
    } catch (error: unknown) {
      if (requestId !== loadRequestId) {
        return
      }

      const status = (error as { response?: { status?: number } })?.response?.status

      if (status === 404) {
        serviceClasses.value = []
        isNotFound.value = true
        pageCount.value = 1
      }
    } finally {
      if (requestId === loadRequestId) {
        isLoading.value = false
      }
    }
  }

  function withOnlineFilter(params: LpuServicesPricesRequest): LpuServicesPricesRequest {
    if (!isOnlineOnly.value) {
      return params
    }

    return {
      ...params,
      is_available_appointment_direct: true,
    }
  }

  function markSearchUsed() {
    if (hasUsedSearch.value) {
      return
    }

    hasUsedSearch.value = true
    sendYaGoal(PRICE_PAGE_GOAL.SEARCH)
  }

  async function searchByText(query: string) {
    markSearchUsed()
    selectedCategoryName.value = ''
    lastSearch.value = { text: query, type: 'search_query' }
    currentPage.value = 1
    await loadClasses(withOnlineFilter({
      lpu_id: context.lpu.id,
      search_query: query,
    }))
  }

  async function searchByClass(name: string) {
    markSearchUsed()
    selectedCategoryName.value = name
    lastSearch.value = { text: name, type: 'service_category_name' }
    currentPage.value = 1
    await loadClasses(withOnlineFilter({
      lpu_id: context.lpu.id,
      service_category_name: name,
    }))
  }

  async function resetSearch() {
    selectedCategoryName.value = ''
    lastSearch.value = { text: '', type: 'search_query' }
    currentPage.value = 1
    await loadClasses(withOnlineFilter({
      lpu_id: context.lpu.id,
    }))
  }

  async function relocatePage(page: number) {
    currentPage.value = page

    const payload: LpuServicesPricesRequest = {
      lpu_id: context.lpu.id,
      page,
    }

    payload[lastSearch.value.type] = lastSearch.value.text
    await loadClasses(withOnlineFilter(payload))
  }

  async function setOnlineOnly(value: boolean) {
    isOnlineOnly.value = value
    currentPage.value = 1

    const payload: LpuServicesPricesRequest = {
      lpu_id: context.lpu.id,
    }

    payload[lastSearch.value.type] = lastSearch.value.text
    await loadClasses(withOnlineFilter(payload))
  }

  async function showAllServices() {
    await setOnlineOnly(false)
    await nextTick()
    smoothScrollToSelector('#prices')
  }

  return {
    serviceCategories,
    serviceClasses,
    pageCount,
    currentPage,
    isNotFound,
    isLoading,
    isOnlineOnly,
    selectedCategoryName,
    hasOnlineSwitch,
    showPagination,
    showAllButton,
    searchByText,
    searchByClass,
    resetSearch,
    relocatePage,
    setOnlineOnly,
    showAllServices,
  }
}
